import {
  ApiErrorSchema,
  AssignPlaylistRequestSchema,
  PlayContentOnScreenRequestSchema,
  PlayContentOnScreenResponseSchema,
  CreateLocationRequestSchema,
  UpdateLocationRequestSchema,
  CreateMediaAssetRequestSchema,
  CreateMediaAssetResponseSchema,
  CreateInvitesRequestSchema,
  CreateOrganisationRequestSchema,
  CreatePlaylistRequestSchema,
  UpdatePlaylistRequestSchema,
  UpdatePlaylistItemRequestSchema,
  CreateScreenRequestSchema,
  CreateTeamRequestSchema,
  CompleteMediaAssetRequestSchema,
  BatchRecipientsResponseSchema,
  CreateDeviceCommandBatchRequestSchema,
  CreateDeviceCommandBatchResponseSchema,
  DeviceCommandBatchStatusSchema,
  CommandOperationsSummarySchema,
  CommandOperationsRecordsResponseSchema,
  DeviceCommandHistoryResponseSchema,
  DeviceCommandStatusSchema,
  DeviceSummarySchema,
  EnqueueDeviceCommandResponseSchema,
  DeviceDisplaySampleSchema,
  EnqueueDeviceCommandRequestSchema,
  LocationSchema,
  MediaAssetSchema,
  OrganisationInviteSchema,
  OrganisationMembershipSchema,
  OrganisationMemberSchema,
  OrganisationSchema,
  PairingCodeResponseSchema,
  AddPlaylistItemRequestSchema,
  PlaylistItemSchema,
  PlaylistPublishStateSchema,
  PlaylistSchema,
  PlaylistVersionDetailSchema,
  PublishPlaylistResponseSchema,
  ReorderPlaylistItemsRequestSchema,
  RollbackPlaylistRequestSchema,
  CreateScheduleRequestSchema,
  UpdateOrganisationRequestSchema,
  UpdateScheduleRequestSchema,
  ScheduleSchema,
  ResolveScheduleRequestSchema,
  ResolveScheduleResponseSchema,
  ScreenSchema,
  ScreenAssignmentSchema,
  CreateScreenGroupRequestSchema,
  UpdateScreenRequestSchema,
  UpdateScreenGroupRequestSchema,
  AddScreenGroupMemberRequestSchema,
  ScreenGroupSchema,
  ScreenGroupDetailSchema,
  CreateLayoutRequestSchema,
  UpdateLayoutRequestSchema,
  LayoutSchema,
  CreateDesignRequestSchema,
  RequestDesignChangesSchema,
  DesignRequestSchema,
  ApprovePairingSessionRequestSchema,
  ApprovePairingSessionResponseSchema,
  CreateVideoWallRequestSchema,
  UpdateVideoWallRequestSchema,
  AssignVideoWallTileRequestSchema,
  PublishVideoWallRequestSchema,
  VideoWallSchema,
  VideoWallDetailSchema,
  VideoWallPublicationSchema,
  TeamSchema,
  UpdateTeamRequestSchema,
  PlaybackEventSchema,
  PlaybackSummarySchema,
  ReportSummarySchema,
  MembershipAlertSettingsSchema,
  UpdateMembershipAlertSettingsSchema,
  OrganisationSettingsSchema,
  UpdateOrganisationSettingsRequestSchema,
  SearchResultsSchema,
  UserProfileSchema,
  UpdateUserProfileRequestSchema,
  UpdateMediaAssetRequestSchema,
  type CreateLocationRequest,
  type UpdateLocationRequest,
  type CreateMediaAssetRequest,
  type CreateInvitesRequest,
  type CreateOrganisationRequest,
  type CreatePlaylistRequest,
  type UpdatePlaylistRequest,
  type UpdatePlaylistItemRequest,
  type CreateScreenRequest,
  type CreateTeamRequest,
  type CompleteMediaAssetRequest,
  type UpdateMediaAssetRequest,
  type AddPlaylistItemRequest,
  type CreateDeviceCommandBatchRequest,
  type EnqueueDeviceCommandRequest,
  type RollbackPlaylistRequest,
  type ReorderPlaylistItemsRequest,
  type CreateScheduleRequest,
  type UpdateOrganisationRequest,
  type UpdateScheduleRequest,
  type ResolveScheduleRequest,
  type CreateScreenGroupRequest,
  type UpdateScreenRequest,
  type UpdateScreenGroupRequest,
  type UpdateTeamRequest,
  type AddScreenGroupMemberRequest,
  type CreateLayoutRequest,
  type UpdateLayoutRequest,
  type CreateDesignRequest,
  type RequestDesignChanges,
  type ApprovePairingSessionRequest,
  type CreateVideoWallRequest,
  type UpdateVideoWallRequest,
  type AssignVideoWallTileRequest,
  type PublishVideoWallRequest,
  type UpdateMembershipAlertSettings,
  type UpdateOrganisationSettingsRequest,
  type UpdateUserProfileRequest,
} from '@ghostscreen/contracts';
import type { z } from 'zod';
import { getToken } from './session';

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3000';

export class ApiRequestError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string | undefined,
    message: string,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
}

async function rawFetch(path: string, options: RequestOptions): Promise<Response> {
  const token = getToken();
  const hasBody = options.body !== undefined;
  return fetch(`${BASE_URL}${path}`, {
    method: options.method ?? 'GET',
    headers: {
      ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers ?? {}),
    },
    ...(hasBody ? { body: JSON.stringify(options.body) } : {}),
  });
}

async function throwIfError(response: Response): Promise<string> {
  const text = await response.text();
  if (response.ok) return text;
  const parsed: unknown = text ? JSON.parse(text) : undefined;
  const error = ApiErrorSchema.safeParse(parsed);
  throw new ApiRequestError(
    response.status,
    error.success ? error.data.error.code : undefined,
    error.success ? error.data.error.message : `Request failed with status ${response.status}`,
  );
}

async function request<T>(
  path: string,
  schema: z.ZodType<T, z.ZodTypeDef, unknown>,
  options: RequestOptions = {},
): Promise<T> {
  const text = await throwIfError(await rawFetch(path, options));
  return schema.parse(text ? JSON.parse(text) : undefined);
}

async function requestVoid(path: string, options: RequestOptions = {}): Promise<void> {
  await throwIfError(await rawFetch(path, options));
}

const org = (organisationId: string, path: string) => `/v1/organisations/${organisationId}${path}`;

export const api = {
  // Signed-in person's profile
  getMyProfile: () => request('/v1/me', UserProfileSchema),
  updateMyProfile: (body: UpdateUserProfileRequest) => {
    UpdateUserProfileRequestSchema.parse(body);
    return request('/v1/me', UserProfileSchema, { method: 'PATCH', body });
  },

  // Organisations
  listMyOrganisations: () => request('/v1/organisations', OrganisationMembershipSchema.array()),
  createOrganisation: (body: CreateOrganisationRequest) => {
    CreateOrganisationRequestSchema.parse(body);
    return request('/v1/organisations', OrganisationSchema, { method: 'POST', body });
  },
  updateOrganisation: (organisationId: string, body: UpdateOrganisationRequest) => {
    UpdateOrganisationRequestSchema.parse(body);
    return request(`/v1/organisations/${organisationId}`, OrganisationSchema, {
      method: 'PATCH',
      body,
    });
  },
  getOrganisation: (organisationId: string) =>
    request(`/v1/organisations/${organisationId}`, OrganisationSchema),
  listOrganisationMembers: (organisationId: string) =>
    request(org(organisationId, '/members'), OrganisationMemberSchema.array()),

  // Teams (people access groups — distinct from screen groups)
  listTeams: (organisationId: string) => request(org(organisationId, '/teams'), TeamSchema.array()),
  createTeam: (organisationId: string, body: CreateTeamRequest) => {
    CreateTeamRequestSchema.parse(body);
    return request(org(organisationId, '/teams'), TeamSchema, { method: 'POST', body });
  },
  updateTeam: (organisationId: string, teamId: string, body: UpdateTeamRequest) => {
    UpdateTeamRequestSchema.parse(body);
    return request(org(organisationId, `/teams/${teamId}`), TeamSchema, {
      method: 'PATCH',
      body,
    });
  },
  deleteTeam: (organisationId: string, teamId: string) =>
    requestVoid(org(organisationId, `/teams/${teamId}`), { method: 'DELETE' }),

  // Organisation invites
  listInvites: (organisationId: string) =>
    request(org(organisationId, '/invites'), OrganisationInviteSchema.array()),
  createInvites: (organisationId: string, body: CreateInvitesRequest) => {
    CreateInvitesRequestSchema.parse(body);
    return request(org(organisationId, '/invites'), OrganisationInviteSchema.array(), {
      method: 'POST',
      body,
    });
  },
  resendInvite: (organisationId: string, inviteId: string) =>
    request(org(organisationId, `/invites/${inviteId}/resend`), OrganisationInviteSchema, {
      method: 'POST',
    }),
  revokeInvite: (organisationId: string, inviteId: string) =>
    request(org(organisationId, `/invites/${inviteId}/revoke`), OrganisationInviteSchema, {
      method: 'POST',
    }),

  // Locations
  listLocations: (organisationId: string) =>
    request(org(organisationId, '/locations'), LocationSchema.array()),
  createLocation: (organisationId: string, body: CreateLocationRequest) => {
    CreateLocationRequestSchema.parse(body);
    return request(org(organisationId, '/locations'), LocationSchema, { method: 'POST', body });
  },
  updateLocation: (organisationId: string, locationId: string, body: UpdateLocationRequest) => {
    UpdateLocationRequestSchema.parse(body);
    return request(org(organisationId, `/locations/${locationId}`), LocationSchema, {
      method: 'PATCH',
      body,
    });
  },
  deleteLocation: (organisationId: string, locationId: string) =>
    requestVoid(org(organisationId, `/locations/${locationId}`), { method: 'DELETE' }),

  // Screens
  listScreens: (organisationId: string) =>
    request(org(organisationId, '/screens'), ScreenSchema.array()),
  createScreen: (organisationId: string, body: CreateScreenRequest) => {
    CreateScreenRequestSchema.parse(body);
    return request(org(organisationId, '/screens'), ScreenSchema, { method: 'POST', body });
  },
  updateScreen: (organisationId: string, screenId: string, body: UpdateScreenRequest) => {
    UpdateScreenRequestSchema.parse(body);
    return request(org(organisationId, `/screens/${screenId}`), ScreenSchema, {
      method: 'PATCH',
      body,
    });
  },
  deleteScreen: (organisationId: string, screenId: string) =>
    requestVoid(org(organisationId, `/screens/${screenId}`), { method: 'DELETE' }),
  createPairingCode: (organisationId: string, screenId: string) =>
    request(org(organisationId, `/screens/${screenId}/pairing-codes`), PairingCodeResponseSchema, {
      method: 'POST',
    }),
  approvePairingSession: (
    organisationId: string,
    code: string,
    body: ApprovePairingSessionRequest,
  ) => {
    ApprovePairingSessionRequestSchema.parse(body);
    return request(
      org(organisationId, `/pairing/sessions/${encodeURIComponent(code)}/approve`),
      ApprovePairingSessionResponseSchema,
      { method: 'POST', body },
    );
  },
  assignPlaylistToScreen: (organisationId: string, screenId: string, playlistId: string) => {
    const body = AssignPlaylistRequestSchema.parse({ playlistId });
    return request(org(organisationId, `/screens/${screenId}/assignment`), ScreenAssignmentSchema, {
      method: 'POST',
      body,
    });
  },
  playContentOnScreen: (
    organisationId: string,
    screenId: string,
    body: { mediaAssetId: string; durationSeconds?: number },
  ) => {
    const parsed = PlayContentOnScreenRequestSchema.parse(body);
    return request(
      org(organisationId, `/screens/${screenId}/play-content`),
      PlayContentOnScreenResponseSchema,
      { method: 'POST', body: parsed },
    );
  },
  clearScreenAssignment: (organisationId: string, screenId: string) =>
    requestVoid(org(organisationId, `/screens/${screenId}/assignment`), { method: 'DELETE' }),
  getScreenAssignment: async (organisationId: string, screenId: string) => {
    try {
      return await request(
        org(organisationId, `/screens/${screenId}/assignment`),
        ScreenAssignmentSchema,
      );
    } catch (error) {
      if (error instanceof ApiRequestError && error.status === 404) return null;
      throw error;
    }
  },

  // Screen groups (targeting sets for schedules / bulk ops)
  listScreenGroups: (organisationId: string) =>
    request(org(organisationId, '/screen-groups'), ScreenGroupSchema.array()),
  createScreenGroup: (organisationId: string, body: CreateScreenGroupRequest) => {
    CreateScreenGroupRequestSchema.parse(body);
    return request(org(organisationId, '/screen-groups'), ScreenGroupSchema, {
      method: 'POST',
      body,
    });
  },
  getScreenGroup: (organisationId: string, screenGroupId: string) =>
    request(org(organisationId, `/screen-groups/${screenGroupId}`), ScreenGroupDetailSchema),
  updateScreenGroup: (
    organisationId: string,
    screenGroupId: string,
    body: UpdateScreenGroupRequest,
  ) => {
    UpdateScreenGroupRequestSchema.parse(body);
    return request(org(organisationId, `/screen-groups/${screenGroupId}`), ScreenGroupSchema, {
      method: 'PATCH',
      body,
    });
  },
  deleteScreenGroup: (organisationId: string, screenGroupId: string) =>
    requestVoid(org(organisationId, `/screen-groups/${screenGroupId}`), { method: 'DELETE' }),
  addScreenGroupMember: (
    organisationId: string,
    screenGroupId: string,
    body: AddScreenGroupMemberRequest,
  ) => {
    AddScreenGroupMemberRequestSchema.parse(body);
    return request(
      org(organisationId, `/screen-groups/${screenGroupId}/members`),
      ScreenGroupDetailSchema,
      { method: 'POST', body },
    );
  },
  removeScreenGroupMember: (organisationId: string, screenGroupId: string, screenId: string) =>
    requestVoid(org(organisationId, `/screen-groups/${screenGroupId}/members/${screenId}`), {
      method: 'DELETE',
    }),

  // Screen layouts (split one display into playlist zones)
  listLayouts: (organisationId: string) =>
    request(org(organisationId, '/layouts'), LayoutSchema.array()),
  createLayout: (organisationId: string, body: CreateLayoutRequest) => {
    CreateLayoutRequestSchema.parse(body);
    return request(org(organisationId, '/layouts'), LayoutSchema, {
      method: 'POST',
      body,
    });
  },
  getLayout: (organisationId: string, layoutId: string) =>
    request(org(organisationId, `/layouts/${layoutId}`), LayoutSchema),
  updateLayout: (organisationId: string, layoutId: string, body: UpdateLayoutRequest) => {
    UpdateLayoutRequestSchema.parse(body);
    return request(org(organisationId, `/layouts/${layoutId}`), LayoutSchema, {
      method: 'PATCH',
      body,
    });
  },
  deleteLayout: (organisationId: string, layoutId: string) =>
    requestVoid(org(organisationId, `/layouts/${layoutId}`), { method: 'DELETE' }),

  // Design service requests
  listDesignRequests: (organisationId: string) =>
    request(org(organisationId, '/design-requests'), DesignRequestSchema.array()),
  createDesignRequest: (organisationId: string, body: CreateDesignRequest) => {
    CreateDesignRequestSchema.parse(body);
    return request(org(organisationId, '/design-requests'), DesignRequestSchema, {
      method: 'POST',
      body,
    });
  },
  requestDesignChanges: (
    organisationId: string,
    designRequestId: string,
    body: RequestDesignChanges,
  ) => {
    RequestDesignChangesSchema.parse(body);
    return request(
      org(organisationId, `/design-requests/${designRequestId}/changes`),
      DesignRequestSchema,
      { method: 'POST', body },
    );
  },
  approveDesignRequest: (organisationId: string, designRequestId: string) =>
    request(
      org(organisationId, `/design-requests/${designRequestId}/approve`),
      DesignRequestSchema,
      { method: 'POST' },
    ),

  // Video walls (GhostScreens Canvas — ADR 0007)
  listVideoWalls: (organisationId: string) =>
    request(org(organisationId, '/video-walls'), VideoWallSchema.array()),
  createVideoWall: (organisationId: string, body: CreateVideoWallRequest) => {
    CreateVideoWallRequestSchema.parse(body);
    return request(org(organisationId, '/video-walls'), VideoWallSchema, {
      method: 'POST',
      body,
    });
  },
  getVideoWall: (organisationId: string, videoWallId: string) =>
    request(org(organisationId, `/video-walls/${videoWallId}`), VideoWallDetailSchema),
  updateVideoWall: (organisationId: string, videoWallId: string, body: UpdateVideoWallRequest) => {
    UpdateVideoWallRequestSchema.parse(body);
    return request(org(organisationId, `/video-walls/${videoWallId}`), VideoWallSchema, {
      method: 'PATCH',
      body,
    });
  },
  deleteVideoWall: (organisationId: string, videoWallId: string) =>
    requestVoid(org(organisationId, `/video-walls/${videoWallId}`), { method: 'DELETE' }),
  assignVideoWallTile: (
    organisationId: string,
    videoWallId: string,
    body: AssignVideoWallTileRequest,
  ) => {
    AssignVideoWallTileRequestSchema.parse(body);
    return request(
      org(organisationId, `/video-walls/${videoWallId}/tiles`),
      VideoWallDetailSchema,
      { method: 'PUT', body },
    );
  },
  clearVideoWallTile: (organisationId: string, videoWallId: string, row: number, col: number) =>
    requestVoid(org(organisationId, `/video-walls/${videoWallId}/tiles/${row}/${col}`), {
      method: 'DELETE',
    }),
  publishVideoWall: (
    organisationId: string,
    videoWallId: string,
    body: PublishVideoWallRequest,
  ) => {
    PublishVideoWallRequestSchema.parse(body);
    return request(
      org(organisationId, `/video-walls/${videoWallId}/publish`),
      VideoWallPublicationSchema,
      { method: 'POST', body },
    );
  },
  stopVideoWall: (organisationId: string, videoWallId: string) =>
    request(org(organisationId, `/video-walls/${videoWallId}/stop`), VideoWallDetailSchema, {
      method: 'POST',
    }),

  // Devices
  listDevices: (organisationId: string) =>
    request(org(organisationId, '/devices'), DeviceSummarySchema.array()),
  listDeviceDisplaySamples: (organisationId: string, deviceId: string, limit = 50) =>
    request(
      org(organisationId, `/devices/${deviceId}/display-samples?limit=${limit}`),
      DeviceDisplaySampleSchema.array(),
    ),
  revokeDevice: (organisationId: string, deviceId: string) =>
    requestVoid(org(organisationId, `/devices/${deviceId}/revoke`), { method: 'POST' }),
  sendDeviceCommand: (
    organisationId: string,
    deviceId: string,
    body: EnqueueDeviceCommandRequest,
    idempotencyKey: string,
  ) => {
    EnqueueDeviceCommandRequestSchema.parse(body);
    return request(
      org(organisationId, `/devices/${deviceId}/commands`),
      EnqueueDeviceCommandResponseSchema,
      {
        method: 'POST',
        body,
        headers: { 'Idempotency-Key': idempotencyKey },
      },
    );
  },
  // Device command batches (bulk group actions)
  createDeviceCommandBatch: (
    organisationId: string,
    body: CreateDeviceCommandBatchRequest,
    idempotencyKey: string,
  ) => {
    CreateDeviceCommandBatchRequestSchema.parse(body);
    return request(
      org(organisationId, '/device-command-batches'),
      CreateDeviceCommandBatchResponseSchema,
      {
        method: 'POST',
        body,
        headers: { 'Idempotency-Key': idempotencyKey },
      },
    );
  },
  getDeviceCommandBatch: (organisationId: string, batchId: string) =>
    request(
      org(organisationId, `/device-command-batches/${batchId}`),
      DeviceCommandBatchStatusSchema,
    ),
  listBatchRecipients: (
    organisationId: string,
    batchId: string,
    params: { cursor?: string; limit?: number } = {},
  ) => {
    const search = new URLSearchParams();
    if (params.cursor) search.set('cursor', params.cursor);
    if (params.limit !== undefined) search.set('limit', String(params.limit));
    const suffix = search.size > 0 ? `?${search.toString()}` : '';
    return request(
      org(organisationId, `/device-command-batches/${batchId}/recipients${suffix}`),
      BatchRecipientsResponseSchema,
    );
  },
  getCommandStatus: (organisationId: string, deviceId: string, commandId: string) =>
    request(
      org(organisationId, `/devices/${deviceId}/commands/${commandId}`),
      DeviceCommandStatusSchema,
    ),
  listDeviceCommandHistory: (
    organisationId: string,
    deviceId: string,
    query: { status?: string; type?: string; cursor?: string; limit?: number } = {},
  ) => {
    const params = new URLSearchParams();
    if (query.status) params.set('status', query.status);
    if (query.type) params.set('type', query.type);
    if (query.cursor) params.set('cursor', query.cursor);
    if (query.limit != null) params.set('limit', String(query.limit));
    const qs = params.toString();
    return request(
      org(organisationId, `/devices/${deviceId}/commands${qs ? `?${qs}` : ''}`),
      DeviceCommandHistoryResponseSchema,
    );
  },

  // Command Operations and Reliability Dashboard
  getCommandOperationsSummary: (
    organisationId: string,
    query: {
      range: '24h' | '7d' | '30d';
      status?: string;
      type?: string;
      deviceId?: string;
      screenGroupId?: string;
      origin?: 'individual' | 'batch';
    },
  ) => {
    const params = new URLSearchParams();
    params.set('range', query.range);
    if (query.status) params.set('status', query.status);
    if (query.type) params.set('type', query.type);
    if (query.deviceId) params.set('deviceId', query.deviceId);
    if (query.screenGroupId) params.set('screenGroupId', query.screenGroupId);
    if (query.origin) params.set('origin', query.origin);
    return request(
      org(organisationId, `/command-operations/summary?${params.toString()}`),
      CommandOperationsSummarySchema,
    );
  },
  listCommandOperationsRecords: (
    organisationId: string,
    query: {
      range: '24h' | '7d' | '30d';
      status?: string;
      type?: string;
      deviceId?: string;
      screenGroupId?: string;
      origin?: 'individual' | 'batch';
      cursor?: string;
      limit?: number;
    },
  ) => {
    const params = new URLSearchParams();
    params.set('range', query.range);
    if (query.status) params.set('status', query.status);
    if (query.type) params.set('type', query.type);
    if (query.deviceId) params.set('deviceId', query.deviceId);
    if (query.screenGroupId) params.set('screenGroupId', query.screenGroupId);
    if (query.origin) params.set('origin', query.origin);
    if (query.cursor) params.set('cursor', query.cursor);
    if (query.limit != null) params.set('limit', String(query.limit));
    return request(
      org(organisationId, `/command-operations/records?${params.toString()}`),
      CommandOperationsRecordsResponseSchema,
    );
  },

  // Media
  listMediaAssets: (organisationId: string) =>
    request(org(organisationId, '/media/assets'), MediaAssetSchema.array()),
  createMediaAsset: (organisationId: string, body: CreateMediaAssetRequest) => {
    CreateMediaAssetRequestSchema.parse(body);
    return request(org(organisationId, '/media/assets'), CreateMediaAssetResponseSchema, {
      method: 'POST',
      body,
    });
  },
  completeMediaAsset: (
    organisationId: string,
    assetId: string,
    body: CompleteMediaAssetRequest,
  ) => {
    CompleteMediaAssetRequestSchema.parse(body);
    return request(org(organisationId, `/media/assets/${assetId}/complete`), MediaAssetSchema, {
      method: 'POST',
      body,
    });
  },
  optimizeMediaPlayback: (organisationId: string, assetId: string) =>
    request(org(organisationId, `/media/assets/${assetId}/optimize-playback`), MediaAssetSchema, {
      method: 'POST',
    }),
  updateMediaAsset: (organisationId: string, assetId: string, body: UpdateMediaAssetRequest) => {
    UpdateMediaAssetRequestSchema.parse(body);
    return request(org(organisationId, `/media/assets/${assetId}`), MediaAssetSchema, {
      method: 'PATCH',
      body,
    });
  },
  deleteMediaAsset: (organisationId: string, assetId: string) =>
    requestVoid(org(organisationId, `/media/assets/${assetId}`), { method: 'DELETE' }),

  // Playlists
  listPlaylists: (organisationId: string) =>
    request(org(organisationId, '/playlists'), PlaylistSchema.array()),
  createPlaylist: (organisationId: string, body: CreatePlaylistRequest) => {
    CreatePlaylistRequestSchema.parse(body);
    return request(org(organisationId, '/playlists'), PlaylistSchema, { method: 'POST', body });
  },
  updatePlaylist: (organisationId: string, playlistId: string, body: UpdatePlaylistRequest) => {
    UpdatePlaylistRequestSchema.parse(body);
    return request(org(organisationId, `/playlists/${playlistId}`), PlaylistSchema, {
      method: 'PATCH',
      body,
    });
  },
  deletePlaylist: (organisationId: string, playlistId: string) =>
    requestVoid(org(organisationId, `/playlists/${playlistId}`), { method: 'DELETE' }),
  listPlaylistItems: (organisationId: string, playlistId: string) =>
    request(org(organisationId, `/playlists/${playlistId}/items`), PlaylistItemSchema.array()),
  addPlaylistItem: (organisationId: string, playlistId: string, body: AddPlaylistItemRequest) => {
    AddPlaylistItemRequestSchema.parse(body);
    return request(org(organisationId, `/playlists/${playlistId}/items`), PlaylistItemSchema, {
      method: 'POST',
      body,
    });
  },
  updatePlaylistItem: (
    organisationId: string,
    playlistId: string,
    itemId: string,
    body: UpdatePlaylistItemRequest,
  ) => {
    UpdatePlaylistItemRequestSchema.parse(body);
    return request(
      org(organisationId, `/playlists/${playlistId}/items/${itemId}`),
      PlaylistItemSchema,
      { method: 'PATCH', body },
    );
  },
  removePlaylistItem: (organisationId: string, playlistId: string, itemId: string) =>
    requestVoid(org(organisationId, `/playlists/${playlistId}/items/${itemId}`), {
      method: 'DELETE',
    }),
  reorderPlaylistItems: (
    organisationId: string,
    playlistId: string,
    body: ReorderPlaylistItemsRequest,
  ) => {
    ReorderPlaylistItemsRequestSchema.parse(body);
    return request(
      org(organisationId, `/playlists/${playlistId}/items/order`),
      PlaylistItemSchema.array(),
      { method: 'PUT', body },
    );
  },
  publishPlaylist: (organisationId: string, playlistId: string) =>
    request(
      org(organisationId, `/playlists/${playlistId}/publish`),
      PublishPlaylistResponseSchema,
      {
        method: 'POST',
      },
    ),
  getPlaylistPublishState: (organisationId: string, playlistId: string) =>
    request(
      org(organisationId, `/playlists/${playlistId}/publish-state`),
      PlaylistPublishStateSchema,
    ),
  getPlaylistVersion: (organisationId: string, playlistId: string, version: number) =>
    request(
      org(organisationId, `/playlists/${playlistId}/versions/${version}`),
      PlaylistVersionDetailSchema,
    ),
  rollbackPlaylist: (organisationId: string, playlistId: string, body: RollbackPlaylistRequest) => {
    RollbackPlaylistRequestSchema.parse(body);
    return request(
      org(organisationId, `/playlists/${playlistId}/rollback`),
      PublishPlaylistResponseSchema,
      { method: 'POST', body },
    );
  },

  // Schedules (daypart overrides; devices evaluate offline from sync snapshot)
  listSchedules: (organisationId: string) =>
    request(org(organisationId, '/schedules'), ScheduleSchema.array()),
  createSchedule: (organisationId: string, body: CreateScheduleRequest) => {
    CreateScheduleRequestSchema.parse(body);
    return request(org(organisationId, '/schedules'), ScheduleSchema, { method: 'POST', body });
  },
  getSchedule: (organisationId: string, scheduleId: string) =>
    request(org(organisationId, `/schedules/${scheduleId}`), ScheduleSchema),
  updateSchedule: (organisationId: string, scheduleId: string, body: UpdateScheduleRequest) => {
    UpdateScheduleRequestSchema.parse(body);
    return request(org(organisationId, `/schedules/${scheduleId}`), ScheduleSchema, {
      method: 'PATCH',
      body,
    });
  },
  deleteSchedule: (organisationId: string, scheduleId: string) =>
    requestVoid(org(organisationId, `/schedules/${scheduleId}`), { method: 'DELETE' }),
  resolveSchedule: (organisationId: string, body: ResolveScheduleRequest) => {
    ResolveScheduleRequestSchema.parse(body);
    return request(org(organisationId, '/schedules/resolve'), ResolveScheduleResponseSchema, {
      method: 'POST',
      body,
    });
  },

  listPlaybackEvents: (
    organisationId: string,
    query: { start?: string; end?: string; limit?: number } = {},
  ) => {
    const params = new URLSearchParams();
    if (query.start) params.set('start', query.start);
    if (query.end) params.set('end', query.end);
    if (query.limit != null) params.set('limit', String(query.limit));
    const qs = params.toString();
    return request(
      org(organisationId, `/reports/plays${qs ? `?${qs}` : ''}`),
      PlaybackEventSchema.array(),
    );
  },
  getPlaybackSummary: (organisationId: string, query: { start?: string; end?: string } = {}) => {
    const params = new URLSearchParams();
    if (query.start) params.set('start', query.start);
    if (query.end) params.set('end', query.end);
    const qs = params.toString();
    return request(
      org(organisationId, `/reports/summary${qs ? `?${qs}` : ''}`),
      PlaybackSummarySchema,
    );
  },
  getReportSummary: (organisationId: string, query: { days: number }) => {
    const params = new URLSearchParams({ days: String(query.days) });
    return request(
      org(organisationId, `/reports/report?${params.toString()}`),
      ReportSummarySchema,
    );
  },
  exportPlaybackCsvUrl: (organisationId: string) =>
    `${BASE_URL}${org(organisationId, '/reports/export')}`,

  getOrganisationSettings: (organisationId: string) =>
    request(org(organisationId, '/settings/organisation'), OrganisationSettingsSchema),
  updateOrganisationSettings: (organisationId: string, body: UpdateOrganisationSettingsRequest) => {
    UpdateOrganisationSettingsRequestSchema.parse(body);
    return request(org(organisationId, '/settings/organisation'), OrganisationSettingsSchema, {
      method: 'PATCH',
      body,
    });
  },

  getAlertSettings: (organisationId: string) =>
    request(org(organisationId, '/settings/alerts'), MembershipAlertSettingsSchema),
  updateAlertSettings: (organisationId: string, body: UpdateMembershipAlertSettings) => {
    UpdateMembershipAlertSettingsSchema.parse(body);
    return request(org(organisationId, '/settings/alerts'), MembershipAlertSettingsSchema, {
      method: 'PATCH',
      body,
    });
  },

  search: (organisationId: string, q: string) =>
    request(
      org(organisationId, `/search?${new URLSearchParams({ q }).toString()}`),
      SearchResultsSchema,
    ),
};

/** Uploads raw bytes straight to the signed URL — the API never sees the file. */
export async function uploadToSignedUrl(uploadUrl: string, file: File): Promise<void> {
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
    body: file,
  });
  if (!response.ok) {
    throw new Error(`Upload failed with status ${response.status}`);
  }
}

export async function sha256Hex(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buffer);
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}
